import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ServiceHandler } from '@nucleus/ui-base';
import { RequestHandler } from '../request-handler';
import { UserDataTypeConfigControllerService } from '../../api/user-data-service';
import {
    ReExtractAttributesResult,
    UserDataTypeAttribute,
    UserDataTypeAttributeCreateRequest,
    UserDataTypeAttributeUpdateRequest,
    UserDataTypeDetail,
    UserDataTypeOptions,
    UserDataTypeSummary,
    UserDataTypeTemplate,
    UserDataTypeTemplateCreateRequest,
    UserDataTypeTemplateUpdateRequest,
} from './userdata-type.models';

/**
 * UserData type-administration backend bridge.
 *
 * Delegates to the OpenAPI-generated UserDataTypeConfigControllerService
 * under src/app/api/user-data-service. BASE_PATH is registered in
 * app.module as '/api/user-data-service' so the BFF proxy correctly
 * targets tasksense-userdata-service through the gateway with the
 * existing OAuth bearer-from-cookie translation.
 *
 * The public callback-based ServiceHandler / RequestHandler shape is
 * preserved; only the transport changed (no consumer churn).
 */
@Injectable({ providedIn: 'root' })
export class UserDataTypeService {

    private requestHandler: RequestHandler = inject(RequestHandler);
    private api: UserDataTypeConfigControllerService = inject(UserDataTypeConfigControllerService);

    // -- Listing / detail ----------------------------------------------------

    listObserved(handler: ServiceHandler<UserDataTypeSummary[]>): void {
        this.requestHandler.handleRequest(this.observed$(), handler, false);
    }

    getDetail(handler: ServiceHandler<UserDataTypeDetail>, type: string): void {
        this.requestHandler.handleRequest(this.detail$(type), handler, false);
    }

    // -- Attributes ----------------------------------------------------------

    listAttributes(handler: ServiceHandler<UserDataTypeAttribute[]>, type: string): void {
        this.requestHandler.handleRequest(this.attributes$(type), handler, false);
    }

    createAttribute(
        handler: ServiceHandler<UserDataTypeAttribute>,
        type: string,
        body: UserDataTypeAttributeCreateRequest,
    ): void {
        this.requestHandler.handleRequest(this.createAttribute$(type, body), handler);
    }

    updateAttribute(
        handler: ServiceHandler<UserDataTypeAttribute>,
        type: string,
        name: string,
        body: UserDataTypeAttributeUpdateRequest,
    ): void {
        this.requestHandler.handleRequest(this.updateAttribute$(type, name, body), handler);
    }

    deleteAttribute(handler: ServiceHandler<void>, type: string, name: string): void {
        this.requestHandler.handleRequest(this.deleteAttribute$(type, name), handler);
    }

    reExtractAttributes(
        handler: ServiceHandler<ReExtractAttributesResult>,
        type: string,
    ): void {
        this.requestHandler.handleRequest(this.reExtract$(type), handler, true);
    }

    // -- Templates -----------------------------------------------------------

    listTemplates(handler: ServiceHandler<UserDataTypeTemplate[]>, type: string): void {
        this.requestHandler.handleRequest(this.templates$(type), handler, false);
    }

    listIncomingTemplates(handler: ServiceHandler<UserDataTypeTemplate[]>, type: string): void {
        this.requestHandler.handleRequest(this.templatesIncoming$(type), handler, false);
    }

    createTemplate(
        handler: ServiceHandler<UserDataTypeTemplate>,
        type: string,
        body: UserDataTypeTemplateCreateRequest,
    ): void {
        this.requestHandler.handleRequest(this.createTemplate$(type, body), handler);
    }

    updateTemplate(
        handler: ServiceHandler<UserDataTypeTemplate>,
        type: string,
        targetPath: string,
        body: UserDataTypeTemplateUpdateRequest,
    ): void {
        this.requestHandler.handleRequest(this.updateTemplate$(type, targetPath, body), handler);
    }

    deleteTemplate(handler: ServiceHandler<void>, type: string, targetPath: string): void {
        this.requestHandler.handleRequest(this.deleteTemplate$(type, targetPath), handler);
    }

    // -- Type options (strict-mode toggle) -----------------------------------

    getOptions(handler: ServiceHandler<UserDataTypeOptions>, type: string): void {
        this.requestHandler.handleRequest(this.options$(type), handler, false);
    }

    setOptions(
        handler: ServiceHandler<UserDataTypeOptions>,
        type: string,
        strict: boolean,
    ): void {
        this.requestHandler.handleRequest(this.setOptions$(type, strict), handler);
    }

    private options$(type: string): Observable<UserDataTypeOptions> {
        return this.api.getOptions(type) as Observable<UserDataTypeOptions>;
    }
    private setOptions$(type: string, strict: boolean): Observable<UserDataTypeOptions> {
        return this.api.setOptions(type, { strict }) as Observable<UserDataTypeOptions>;
    }

    /**
     * v2 options setter — sends the full {@link UserDataTypeOptions} payload
     * (resource defaults, cost defaults, manual-create/edit gates). Calls
     * PUT /options/v2 which coexists with the v1 strict-only PUT until UI
     * cuts over (see Confluence design page 82345987 §7.6).
     */
    setOptionsV2(
        handler: ServiceHandler<UserDataTypeOptions>,
        type: string,
        options: UserDataTypeOptions,
    ): void {
        this.requestHandler.handleRequest(this.setOptionsV2$(type, options), handler);
    }

    private setOptionsV2$(type: string, options: UserDataTypeOptions): Observable<UserDataTypeOptions> {
        return this.api.setOptionsV2(type, options as any) as Observable<UserDataTypeOptions>;
    }

    // -- Raw observable shapers (kept private so the public API only
    //    speaks ServiceHandler<T>) -------------------------------------------

    private observed$(): Observable<UserDataTypeSummary[]> {
        return this.api.listObservedTypes() as Observable<UserDataTypeSummary[]>;
    }

    private detail$(type: string): Observable<UserDataTypeDetail> {
        return this.api.describeType(type) as Observable<UserDataTypeDetail>;
    }

    private attributes$(type: string): Observable<UserDataTypeAttribute[]> {
        return this.api.listAttributes(type) as Observable<UserDataTypeAttribute[]>;
    }

    private createAttribute$(
        type: string,
        body: UserDataTypeAttributeCreateRequest,
    ): Observable<UserDataTypeAttribute> {
        return this.api.createAttribute(type, body as any) as Observable<UserDataTypeAttribute>;
    }

    private updateAttribute$(
        type: string,
        name: string,
        body: UserDataTypeAttributeUpdateRequest,
    ): Observable<UserDataTypeAttribute> {
        return this.api.updateAttribute(type, name, body as any) as Observable<UserDataTypeAttribute>;
    }

    private deleteAttribute$(type: string, name: string): Observable<void> {
        return this.api.deleteAttribute(type, name) as Observable<void>;
    }

    private reExtract$(type: string): Observable<ReExtractAttributesResult> {
        return this.api.reExtract(type) as unknown as Observable<ReExtractAttributesResult>;
    }

    private templates$(type: string): Observable<UserDataTypeTemplate[]> {
        return this.api.listTemplates(type) as Observable<UserDataTypeTemplate[]>;
    }

    private templatesIncoming$(type: string): Observable<UserDataTypeTemplate[]> {
        return this.api.listIncomingTemplates(type) as Observable<UserDataTypeTemplate[]>;
    }

    private createTemplate$(
        type: string,
        body: UserDataTypeTemplateCreateRequest,
    ): Observable<UserDataTypeTemplate> {
        return this.api.createTemplate(type, body as any) as Observable<UserDataTypeTemplate>;
    }

    private updateTemplate$(
        type: string,
        targetPath: string,
        body: UserDataTypeTemplateUpdateRequest,
    ): Observable<UserDataTypeTemplate> {
        // targetPath is a JSONPath ($.foo.bar); the OpenAPI client's
        // encodeParam handles URI encoding so any literal '/' is preserved
        // for the backend mapping {targetPath:.+}.
        return this.api.updateTemplate(type, targetPath, body as any) as Observable<UserDataTypeTemplate>;
    }

    private deleteTemplate$(type: string, targetPath: string): Observable<void> {
        return this.api.deleteTemplate(type, targetPath) as Observable<void>;
    }
}
